import React, { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../context/AuthContext'
import moduleConfig from '../config/moduleConfig'

/**
 * Needs Attention: open items from every module, most urgent first, each
 * linking to where it gets done. Members see their own; anyone who holds a
 * module's admin role can switch to the department view. The server decides
 * what each person may see (server/core/needsAttention.js).
 */

const SEVERITY = {
  critical: { Icon: ExclamationCircleIcon, tone: 'text-red-400', dot: 'bg-red-500' },
  warning: { Icon: ExclamationTriangleIcon, tone: 'text-amber-400', dot: 'bg-amber-500' },
  info: { Icon: InformationCircleIcon, tone: 'text-blue-400', dot: 'bg-blue-500' },
}
const COLLAPSED = 8
const SCOPE_KEY = 'needsAttentionScope'

export default function NeedsAttention() {
  const { getAccessToken } = useAuth()
  // Read through a ref so a new function identity can't trigger a refetch:
  // the list loads when the scope changes, and only then.
  const tokenRef = useRef(getAccessToken)
  tokenRef.current = getAccessToken
  const [scope, setScope] = useState(() => localStorage.getItem(SCOPE_KEY) || 'mine')
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const load = async (which) => {
    setError(false)
    try {
      const token = tokenRef.current()
      const res = await fetch(`/api/needs-attention?scope=${which}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error)
      // A saved Department choice from someone who no longer has it
      if (which === 'department' && !json.canViewDepartment) {
        setScope('mine')
        return
      }
      setData(json)
    } catch {
      setError(true)
    }
  }

  useEffect(() => { setExpanded(false); load(scope) }, [scope]) // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (next) => {
    localStorage.setItem(SCOPE_KEY, next)
    setScope(next)
  }

  const items = data?.items || []
  const shown = expanded ? items : items.slice(0, COLLAPSED)
  const counts = items.reduce((acc, i) => ({ ...acc, [i.severity]: (acc[i.severity] || 0) + 1 }), {})

  return (
    <section className="bg-slate-800 border border-slate-700 rounded-xl">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-slate-700 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            Needs Attention
            {items.length > 0 && (
              <span className={`min-w-[1.5rem] h-6 px-2 rounded-full text-xs font-semibold leading-6 text-center ${counts.critical ? 'bg-red-500 text-white' : 'bg-slate-600 text-white'}`}>
                {items.length}
              </span>
            )}
          </h2>
          <p className="text-slate-500 text-xs mt-0.5">
            {scope === 'department' ? 'Open items across the department' : 'Open items for you'}
          </p>
        </div>
        {data?.canViewDepartment && (
          <div role="tablist" className="inline-flex p-0.5 rounded-lg bg-slate-900/60 border border-slate-700">
            {[['mine', 'Mine'], ['department', 'Department']].map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={scope === id}
                onClick={() => choose(id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${scope === id ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error ? (
        <p className="p-4 text-sm text-red-400">Couldn't load this list. Try again in a moment.</p>
      ) : !data ? (
        <div className="p-6 flex justify-center">
          <div className="w-6 h-6 border-2 border-slate-600 border-t-amber-500 rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center">
          <CheckCircleIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
          <p className="text-slate-300">Nothing needs attention{scope === 'mine' ? ' from you' : ''}.</p>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-slate-700/60">
            {shown.map(item => {
              const s = SEVERITY[item.severity] || SEVERITY.info
              return (
                <li key={item.key}>
                  <Link to={item.link || '/'} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-700/30 transition-colors">
                    <s.Icon className={`w-5 h-5 mt-0.5 shrink-0 ${s.tone}`} aria-label={item.severity} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white">{item.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{item.detail}</p>
                    </div>
                    <span className="hidden sm:block text-[11px] text-slate-500 shrink-0 mt-0.5">
                      {moduleConfig[item.module]?.name || ''}
                    </span>
                    <ChevronRightIcon className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                  </Link>
                </li>
              )
            })}
          </ul>
          {items.length > COLLAPSED && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="w-full px-4 py-2.5 text-sm text-amber-500 hover:text-amber-400 border-t border-slate-700"
            >
              {expanded ? 'Show fewer' : `Show all ${items.length}`}
            </button>
          )}
        </>
      )}
      {data?.unavailable?.length > 0 && (
        <p className="px-4 pb-3 text-xs text-slate-500">
          Some items couldn't be loaded ({data.unavailable.map(m => moduleConfig[m]?.name || m).join(', ')}).
        </p>
      )}
    </section>
  )
}
